import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import IconButton from './components/IconButton';
import ViewActionIcon from './components/ViewActionIcon';
import FacilitiesPage from './FacilitiesPage';
import Pagination from './components/Pagination';
import SortableTableHeader from './components/SortableTableHeader';
import type { ImportRecord, IntegrationsTab, PagedResult } from './types/index';
import type { FieldSortCriterionValue } from './utils/tableSort';
import { getAvailableActions, getResourceActions, getWriteActions } from './utils/permissionCatalog';
import { checkPermissionActions } from './utils/permissions';
import { formatImportDate } from './utils/formatDate';

type ImportsPageProps = {
  organizationId: number | string | null;
  isQuickstartOrganization?: boolean;
  currentUser: any,
  history: PagedResult<ImportRecord>;
  historyError: unknown;
  historyLoading: boolean;
  onAdd?: () => void;
  onPageChange: (offset: number) => void;
  onSort: (fieldName: string) => void;
  onView?: (job: ImportRecord) => void;
  onFacilityImport?: () => void;
  onFacilityEdit?: (facility: any) => void;
  activeTab: IntegrationsTab;
  onTabChange: (tab: IntegrationsTab) => void;
  sort: FieldSortCriterionValue;
};

export default function ImportsPage({
  organizationId,
  isQuickstartOrganization = false,
  currentUser,
  history,
  historyError,
  historyLoading,
  onAdd,
  onPageChange,
  onSort,
  onView,
  onFacilityImport,
  onFacilityEdit,
  activeTab,
  onTabChange,
  sort,
}: ImportsPageProps) {
  const navigate = useNavigate();
  const actions = getAvailableActions(currentUser);
  const canReadImports = checkPermissionActions(actions, getResourceActions('Imports'));
  const canReadFacilities = checkPermissionActions(actions, getResourceActions('Facilities'));
  const canImportFacilities =
    checkPermissionActions(actions, getWriteActions('Facilities')) &&
    checkPermissionActions(actions, getWriteActions('Imports')) &&
    checkPermissionActions(actions, getWriteActions('Products'));
  // Missing permission takes precedence over the Quickstart restriction in the tooltip.
  const importDisabledReason = !canImportFacilities
    ? 'You are not authorized to import facilities'
    : isQuickstartOrganization
      ? 'Importing facilities is not available for Quickstart organizations'
      : undefined;

  return (

    <div className="stack" data-testid="page-imports">
      <button className="button" type="button" onClick={() => navigate('/admin')} data-testid="imports-back-to-admin" style={{ alignSelf: 'flex-start' }}>
        Back
      </button>
      <PageHeader
        title="Integrations"
        subtitle={activeTab === 'facilities' ? 'Facilities' : 'Job History'}
        actions={(
          onAdd ? (
            // Quickstart orgs are label dumps for MCA, so import is disabled even for authorized users; the span carries the tooltip because disabled buttons may not show one.
            <span title={importDisabledReason} style={{ display: 'inline-flex' }}>
            <button className="button cta" onClick={onAdd} disabled={importDisabledReason !== undefined} data-testid="imports-add">
              <span className="plus" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              New Import
            </button>
            </span>
          ) : null
        )}
      />

      <div className="drawer-tabs" role="tablist" aria-label="Integrations tabs">
        {canReadFacilities && <button
          type="button"
          className={`drawer-tab ${activeTab === 'facilities' ? 'active' : ''}`}
          onClick={() => onTabChange('facilities')}
          role="tab"
          aria-selected={activeTab === 'facilities'}
        >
          Facilities
        </button>}
        {canReadImports && <button
          type="button"
          className={`drawer-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => onTabChange('history')}
          role="tab"
          aria-selected={activeTab === 'history'}
        >
          History
        </button>}
      </div>

       {activeTab === 'facilities' && canReadFacilities && (
        <FacilitiesPage
        organizationId={organizationId}
        currentUser={currentUser}
          onImport={onFacilityImport}
          onEdit={onFacilityEdit}
          showImportButton={false}
          showCounts={false}
          showViewToggle={false}
          defaultView="list"
          showHeader={false}
        />
      )}
       {activeTab !== 'facilities' && canReadImports && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <SortableTableHeader label="Type" fieldName="type" sort={sort} onSort={onSort} />
                <SortableTableHeader label="Facilities" fieldName="facilityCount" sort={sort} onSort={onSort} />
                <SortableTableHeader label="Items" fieldName="itemCount" sort={sort} onSort={onSort} />
                <SortableTableHeader label="Date" fieldName="importedAt" sort={sort} onSort={onSort} />
                <th className="col-actions" aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {historyLoading && (
                <tr><td colSpan={5}>Loading...</td></tr>
              )}
              {!historyLoading && historyError && (
                <tr><td colSpan={5} role="alert">Unable to load import history.</td></tr>
              )}
              {history.data.map((job, idx) => (
                <tr key={job.id} data-testid={idx === 0 ? 'import-row-latest' : undefined}>
                  <td data-testid={`import-name-${job.id}`}>{job.type}</td>
                  <td>{Array.isArray(job.facilityDetails) ? job.facilityDetails.length : 0}</td>
                  <td>{(Array.isArray(job.productDetails) && job.productDetails.length) ? job.productDetails.length : (Array.isArray(job.packageDetails) ? job.packageDetails.length : 0)}</td>
                  <td>{formatImportDate(job.importedAt) || '—'}</td>
                  <td className="col-actions">
                    <IconButton
                      title="View"
                      ariaLabel="View"
                      onClick={() => onView && onView(job)}
                      testId={`import-view-${job.id}`}
                    >
                      <ViewActionIcon />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

          {activeTab != 'facilities' &&  < Pagination
              page={history.page}
              totalPages={Math.ceil(history.total / history.pageSize)}
              onPrev={() => onPageChange(-1)}
              onNext={() => onPageChange(1)}
              format="of"
              prevLabel="◀"
              nextLabel="▶"
          />}
    </div>
  );
}
